/**
 * AIReviewPanel — Renders the AI reviewer verdict + flags + extracted
 * fields for a single IDV submission.
 *
 * The component is DEFENSIVELY built to render nothing when the
 * upstream review is missing (older submissions from before the AI
 * pipeline was live). When present it shows:
 *
 *   • Confidence score (colour-coded)
 *   • Recommended action pill (approve / review / reject)
 *   • Flag chips (only shown when flags fire)
 *   • Duplicate warning when detected
 *   • Extracted fields grid (2 col)
 *   • Optional "Re-run AI" button (admin action)
 *
 * Props: { review, duplicateOf, onRerun, testIdSuffix }
 */
import React from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Sparkles, CheckCircle2, XCircle, AlertTriangle, Copy,
  Loader2, RefreshCw, ShieldAlert,
} from 'lucide-react';

const FLAG_LABELS = {
  expired: 'ID expired',
  blurry: 'Blurry image',
  quality_low: 'Low image quality',
  selfie_mismatch: 'Selfie mismatch',
  name_mismatch: 'Name mismatch',
  possible_tampering: 'Possible tampering',
  duplicate: 'Duplicate ID',
  missing_data: 'Missing data',
  unsupported_document: 'Unsupported document',
};

function scoreColor(score) {
  if (score == null) return 'text-slate-500';
  if (score >= 95) return 'text-emerald-700';
  if (score >= 80) return 'text-amber-700';
  return 'text-rose-700';
}

function actionPill(action) {
  const map = {
    approve: { icon: CheckCircle2, klass: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: 'Approve' },
    review:  { icon: AlertTriangle, klass: 'bg-amber-100 text-amber-900 border-amber-300', label: 'Review' },
    reject:  { icon: XCircle, klass: 'bg-rose-100 text-rose-800 border-rose-300', label: 'Reject' },
  };
  const entry = map[action] || map.review;
  const Icon = entry.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${entry.klass}`} data-testid={`ai-action-${action}`}>
      <Icon className="w-3 h-3" /> {entry.label}
    </span>
  );
}

export default function AIReviewPanel({ review, duplicateOf, onRerun, rerunning, testIdSuffix }) {
  if (!review) {
    return (
      <div
        className="mt-3 rounded-md border border-dashed border-slate-300 bg-slate-50 p-3 text-xs text-slate-500 flex items-center gap-2"
        data-testid={`ai-review-missing-${testIdSuffix}`}
      >
        <Sparkles className="w-4 h-4" />
        No AI review recorded yet.
        {onRerun && (
          <Button
            size="sm" variant="outline" className="ml-auto"
            onClick={onRerun} disabled={rerunning}
            data-testid={`ai-rerun-${testIdSuffix}`}
          >
            {rerunning
              ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              : <RefreshCw className="w-3 h-3 mr-1" />}
            Run AI review
          </Button>
        )}
      </div>
    );
  }

  const fields = review.extracted_fields || {};
  const flags = review.flags || [];
  const score = review.confidence_score;
  const scoreStr = score != null ? score.toFixed(1) : '—';

  return (
    <div
      className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-2"
      data-testid={`ai-review-panel-${testIdSuffix}`}
    >
      {/* Header — score + action + auto-approved badge */}
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkles className="w-4 h-4 text-indigo-600" />
        <span className="text-xs font-semibold uppercase tracking-wide text-indigo-900">AI Review</span>
        <span className={`text-lg font-bold ${scoreColor(score)}`} data-testid={`ai-confidence-${testIdSuffix}`}>
          {scoreStr}%
        </span>
        {actionPill(review.recommended_action)}
        {review.auto_approved && (
          <Badge className="bg-emerald-600 text-white" data-testid={`ai-auto-approved-${testIdSuffix}`}>
            Auto-approved
          </Badge>
        )}
        {review.selfie_match_score != null && (
          <span className="text-xs text-slate-600" data-testid={`ai-selfie-match-${testIdSuffix}`}>
            Selfie match: <strong className={scoreColor(review.selfie_match_score)}>{review.selfie_match_score.toFixed(1)}%</strong>
          </span>
        )}
        {onRerun && (
          <Button
            size="sm" variant="ghost" className="ml-auto h-7 px-2"
            onClick={onRerun} disabled={rerunning}
            data-testid={`ai-rerun-${testIdSuffix}`}
          >
            {rerunning
              ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              : <RefreshCw className="w-3 h-3 mr-1" />}
            Re-run
          </Button>
        )}
      </div>

      {/* Flags */}
      {flags.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid={`ai-flags-${testIdSuffix}`}>
          {flags.map((f) => (
            <Badge
              key={f}
              className="bg-rose-100 text-rose-800 border border-rose-300 inline-flex items-center gap-1"
              data-testid={`ai-flag-${f}-${testIdSuffix}`}
            >
              <AlertTriangle className="w-3 h-3" /> {FLAG_LABELS[f] || f}
            </Badge>
          ))}
        </div>
      )}

      {/* Duplicate warning */}
      {duplicateOf && (
        <div
          className="rounded-md border border-rose-300 bg-rose-50 p-2 text-xs text-rose-900 flex items-start gap-2"
          data-testid={`ai-duplicate-warning-${testIdSuffix}`}
        >
          <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">Duplicate ID detected</div>
            <div>
              Matches existing account: <strong>{duplicateOf.name || duplicateOf.email}</strong>
              {duplicateOf.idv_status && ` (${duplicateOf.idv_status})`}
              {duplicateOf.account_created && ` · joined ${new Date(duplicateOf.account_created).toLocaleDateString()}`}
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      {review.summary && (
        <div className="text-xs text-slate-700 italic" data-testid={`ai-summary-${testIdSuffix}`}>
          {review.summary}
        </div>
      )}

      {/* Extracted fields */}
      {Object.keys(fields).length > 0 && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs pt-2 border-t border-indigo-100" data-testid={`ai-fields-${testIdSuffix}`}>
          {[
            ['document_number', 'Doc #'],
            ['first_name', 'First'],
            ['last_name', 'Last'],
            ['dob', 'DOB'],
            ['expiration_date', 'Expires'],
            ['document_type', 'Type'],
          ].map(([k, label]) => (
            <div key={k}>
              <span className="text-slate-500">{label}: </span>
              <span className="text-slate-900 font-medium">{fields[k] || '—'}</span>
            </div>
          ))}
        </div>
      )}

      {/* Error banner (AI itself errored) */}
      {review.error && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid={`ai-error-${testIdSuffix}`}>
          AI reviewer error — this submission was routed to manual review: {review.error}
        </div>
      )}
    </div>
  );
}
